import React, { useEffect, useMemo, useRef, useState } from "react"

import { Spin } from "antd"
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  PointElement,
  TimeScale,
  Title,
  Tooltip
} from "chart.js"
import { Bar } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { countryCodesByCountry } from "../../../Components/Filters/filterOptionsData"
import { countryChartOptions } from "./chartsOptions"
import { countryFlagAxisPlugin } from "./chartsPlagins"

ChartJS.register(
  TimeScale,
  LinearScale,
  CategoryScale,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend
)

ChartJS.defaults.color = "#667085"

let alertsCountriesDataEmpty = {
  labels: [],
  result: {
    data: [],
    count: [1000, 2000, 3000, 4000]
  },
  total_count: null,
  total_volume: null,
  start_date: "Jan 17, 2024",
  end_date: "Jan 25, 2024"
}

const AlertsCountryChart = ({ full, isMobile }) => {
  const { t } = useTranslation()
  const chartRef = useRef(null)

  const { alertsCountryFetching, alertsCountryData } = useSelector((state) => state.dashboard)
  const theme = useSelector((state) => state.nav.theme)

  const [hoverIndex, setHoverIndex] = useState(null)

  // Sized so the chart fills the fixed-height card it sits in. Bars are a fixed 22px, so
  // the canvas has to be divided among enough countries for them to sit close together:
  // the dashboard asks the API for the top 13, which is what this height is cut for.
  const CHART_HEIGHT = 506
  const chartHeight =
    full && alertsCountryData?.tableData?.length > 13
      ? alertsCountryData?.tableData?.length * 38
      : CHART_HEIGHT

  //udate chart after changing browser tab
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible" && chartRef.current) {
      chartRef.current.update()
    }
  }

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  const chartOptions = useMemo(() => {
    let period = [alertsCountryData?.start_date || null, alertsCountryData?.end_date || null]
    return countryChartOptions(period, setHoverIndex, theme, isMobile)
  }, [alertsCountryData, theme, isMobile])

  const getChartData = (data) => {
    const color = {
      dark: {
        color: "rgba(34, 90, 234, 1)",
        color_hover: "rgba(34, 90, 234, 0.5)"
      },
      light: {
        color: "rgba(147, 193, 253, 1)",
        color_hover: "rgba(147, 193, 253, 0.5)"
      }
    }

    return {
      labels: data?.labels,
      datasets: [
        {
          // label: 'Web',
          data: data?.result?.count,
          count: data?.result?.count,
          volume: data?.result?.volume,
          // Read by the country-flag-axis plugin, aligned with `labels`.
          codes: data?.labels?.map((name) => countryCodesByCountry[name]),
          backgroundColor: data?.result?.count?.map((_, index) =>
            hoverIndex !== null && hoverIndex !== index
              ? color?.[theme]?.color_hover
              : color?.[theme]?.color
          ),
          borderRadius: [{ topLeft: 0, topRight: 2, bottomLeft: 0, bottomRight: 2 }],
          borderSkipped: false,
          pointHoverRadius: 2,
          yAxisID: "y",
          barThickness: 22
          //borderWidth: 2,
          //backgroundColor: '#93C1FD',
          //borderColor: '#7471F6',
        }
      ]
    }
  }

  if (!alertsCountryData) {
    const chartData = getChartData(alertsCountriesDataEmpty)
    return (
      <Spin spinning={!!alertsCountryFetching}>
        <div className="card-empty-state-wrapper">
          <Bar
            ref={chartRef}
            options={chartOptions}
            data={chartData}
            height={506}
            plugins={[countryFlagAxisPlugin]}
          />
          <span className="empty-state-title">{t("No data available")}</span>
        </div>
      </Spin>
    )
  }

  const chartData = getChartData(alertsCountryData)

  return (
    <Spin spinning={!!alertsCountryFetching}>
      <Bar
        key={chartHeight}
        ref={chartRef}
        options={chartOptions}
        data={chartData}
        height={chartHeight}
        plugins={[countryFlagAxisPlugin]}
      />
    </Spin>
  )
}

export default AlertsCountryChart
